/**
 * Control-plane API client.
 * - credentials: 'include' always (BFF httpOnly session cookie)
 * - CSRF: read inbox_csrf cookie → x-csrf-token on POST/DELETE/PUT/PATCH
 * - Never stores Cognito tokens in localStorage
 */

import {
  ApiError,
  DEFAULT_FEATURE_FLAGS,
  type Account,
  type AdminFlag,
  type AdminInvite,
  type AdminInviteCreated,
  type AdminUser,
  type ApiErrorBody,
  type AuditEvent,
  type BillingSummary,
  type CreateUploadResponse,
  type FeatureFlags,
  type FlagStatus,
  type LoginResponse,
  type McpClient,
  type MintedPat,
  type PatMeta,
  type PatScope,
  type UploadSession,
  type UsageSummary,
  type UserProfile,
  type UserRole,
} from "./types.js";
import {
  apiBaseUrl,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
} from "./config.js";

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Read a cookie value by name (document.cookie). */
export function getCookie(name: string, cookieSource?: string): string | null {
  const raw =
    cookieSource ??
    (typeof document !== "undefined" ? document.cookie : "");
  if (!raw) return null;
  const parts = raw.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq < 0) continue;
    const key = trimmed.slice(0, eq).trim();
    if (key === name) {
      return decodeURIComponent(trimmed.slice(eq + 1).trim());
    }
  }
  return null;
}

export function getCsrfToken(cookieSource?: string): string | null {
  return getCookie(CSRF_COOKIE_NAME, cookieSource);
}

export function buildUrl(path: string): string {
  const base = apiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${p}` : p;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: "invalid_json", error_description: text.slice(0, 200) };
  }
}

function errorFromBody(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as ApiErrorBody;
  const code = typeof b.error === "string" ? b.error : `http_${status}`;
  const message =
    typeof b.error_description === "string"
      ? b.error_description
      : typeof b.error === "string"
        ? b.error
        : `Request failed (${status})`;
  return new ApiError(status, code, message, body);
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  /** Skip CSRF header (only for login, which uses Origin check). */
  skipCsrf?: boolean;
};

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    accept: "application/json",
  };

  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
  }

  if (MUTATING.has(method) && !opts.skipCsrf) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers[CSRF_HEADER_NAME] = csrf;
    }
  }

  const res = await fetch(buildUrl(path), {
    method,
    credentials: "include",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const body = await parseBody(res);
  if (!res.ok) {
    throw errorFromBody(res.status, body);
  }
  return body as T;
}

// ── Auth ──────────────────────────────────────────────────────────────────

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  // Login CSRF uses Origin/Referer on the server — no prior csrf cookie.
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: { email, password },
  });
}

/** Start forgot-password (Cognito emails a code). Never reveals if email exists. */
export async function forgotPassword(
  email: string,
): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/forgot-password", {
    method: "POST",
    skipCsrf: true,
    body: { email },
  });
}

/** Complete forgot-password with email code + new password. */
export async function confirmForgotPassword(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/confirm-forgot-password", {
    method: "POST",
    skipCsrf: true,
    body: {
      email: input.email,
      code: input.code,
      newPassword: input.newPassword,
    },
  });
}

export async function respondNewPassword(input: {
  session: string;
  email: string;
  newPassword: string;
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: {
      challengeName: "NEW_PASSWORD_REQUIRED",
      session: input.session,
      email: input.email,
      newPassword: input.newPassword,
    },
  });
}

export async function respondTotp(input: {
  session: string;
  email: string;
  totpCode: string;
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: {
      challengeName: "SOFTWARE_TOKEN_MFA",
      session: input.session,
      email: input.email,
      totpCode: input.totpCode,
    },
  });
}

export async function logout(): Promise<void> {
  await apiRequest<null>("/api/v1/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<UserProfile> {
  return apiRequest<UserProfile>("/api/v1/auth/me");
}

/** PATCH /api/v1/auth/me — update display name (empty string clears). */
export async function updateProfile(input: {
  displayName: string;
}): Promise<UserProfile> {
  return apiRequest<UserProfile>("/api/v1/auth/me", {
    method: "PATCH",
    body: input,
  });
}

/** POST /api/v1/auth/change-password — signed-in password change. */
export async function changePassword(input: {
  previousPassword: string;
  newPassword: string;
}): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/change-password", {
    method: "POST",
    body: input,
  });
}

// ── Accounts ──────────────────────────────────────────────────────────────

export async function listAccounts(): Promise<Account[]> {
  const res = await apiRequest<{ accounts: Account[] }>("/api/v1/accounts");
  return res.accounts ?? [];
}

export type CreateAiAccountBody = {
  provider: string;
  label: string;
  apiKey: string;
  defaults?: {
    model?: string;
    reasoningEffort?: "none" | "low" | "medium" | "high";
  };
};

/** Connect an AI provider API key (never log body). */
export async function createAiAccount(
  body: CreateAiAccountBody,
): Promise<Account> {
  return apiRequest<Account>("/api/v1/accounts", {
    method: "POST",
    body,
  });
}

/** @deprecated use createAiAccount */
export async function createImapAccount(
  body: CreateAiAccountBody,
): Promise<Account> {
  return createAiAccount(body);
}

export async function deleteAccount(accountId: string): Promise<void> {
  await apiRequest<null>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`,
    { method: "DELETE" },
  );
}

export type UpdateAccountBody = {
  label?: string;
  defaults?: {
    model?: string | null;
    reasoningEffort?: "none" | "low" | "medium" | "high" | null;
  } | null;
};

export async function updateAccount(
  accountId: string,
  body: UpdateAccountBody,
): Promise<Account> {
  return apiRequest<Account>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`,
    { method: "PATCH", body },
  );
}

export async function rotateAccountKey(
  accountId: string,
  apiKey: string,
): Promise<Account> {
  return apiRequest<Account>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/key`,
    { method: "PUT", body: { apiKey } },
  );
}

export async function verifyAccount(accountId: string): Promise<Account> {
  return apiRequest<Account>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/verify`,
    { method: "POST", body: {} },
  );
}

export type ProviderModelInfo = {
  id: string;
  kind: string;
  createdAt?: string;
};

export type ListModelsResponse = {
  provider: string;
  accountId?: string;
  models: ProviderModelInfo[];
};

/** List models for a connected account (uses stored API key). */
export async function listAccountModels(
  accountId: string,
): Promise<ListModelsResponse> {
  return apiRequest<ListModelsResponse>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}/models`,
    { method: "GET" },
  );
}

/**
 * List models with a not-yet-saved key (connect form).
 * Does not persist the key.
 */
export async function previewModels(
  provider: string,
  apiKey: string,
): Promise<ListModelsResponse> {
  return apiRequest<ListModelsResponse>("/api/v1/accounts/preview-models", {
    method: "POST",
    body: { provider, apiKey },
  });
}

/** Gmail OAuth removed from mcpto.ai. */
export async function startGmailOauth(): Promise<never> {
  throw new ApiError(
    410,
    "gone",
    "Gmail OAuth is not available on mcpto.ai",
  );
}

// ── Features ──────────────────────────────────────────────────────────────

/**
 * Session-authenticated public feature flags (B0).
 * Falls back to client defaults only when the caller opts in via tryGetFeatures.
 */
export async function getFeatures(): Promise<FeatureFlags> {
  const res = await apiRequest<{ features: FeatureFlags }>("/api/v1/features");
  return { ...DEFAULT_FEATURE_FLAGS, ...(res.features ?? {}) };
}

/**
 * Best-effort features fetch for SPA boot.
 * Missing route / network → defaults (scopesEnforce off).
 */
export async function tryGetFeatures(): Promise<FeatureFlags> {
  try {
    return await getFeatures();
  } catch {
    return { ...DEFAULT_FEATURE_FLAGS };
  }
}

// ── Billing & usage ───────────────────────────────────────────────────────

export async function getBilling(): Promise<BillingSummary> {
  return apiRequest<BillingSummary>("/api/v1/billing/me");
}

export async function tryGetBilling(): Promise<BillingSummary | null> {
  try {
    return await getBilling();
  } catch {
    return null;
  }
}

export async function startCheckout(input: {
  plan: "monthly" | "annual";
  promoCode?: string;
}): Promise<{ checkoutUrl: string | null; sessionId: string }> {
  return apiRequest("/api/v1/billing/checkout", {
    method: "POST",
    body: input,
  });
}

export async function openBillingPortal(): Promise<{ portalUrl: string }> {
  return apiRequest("/api/v1/billing/portal", { method: "POST", body: {} });
}

export async function redeemPromo(
  code: string,
): Promise<{ promo: { code: string; type: string; value: number }; billing: BillingSummary }> {
  return apiRequest("/api/v1/billing/redeem-promo", {
    method: "POST",
    body: { code },
  });
}

export async function getUsage(): Promise<UsageSummary> {
  return apiRequest<UsageSummary>("/api/v1/usage");
}

export async function tryGetUsage(): Promise<UsageSummary | null> {
  try {
    return await getUsage();
  } catch {
    return null;
  }
}

export async function signup(input: {
  email: string;
  password: string;
  promoCode?: string;
  referralCode?: string;
}): Promise<{ status: string; message?: string }> {
  return apiRequest("/api/v1/auth/signup", {
    method: "POST",
    body: input,
    skipCsrf: true,
  });
}

export async function confirmSignup(input: {
  email: string;
  code: string;
}): Promise<{ status: string; message?: string }> {
  return apiRequest("/api/v1/auth/confirm-signup", {
    method: "POST",
    body: input,
    skipCsrf: true,
  });
}

export async function resendConfirmation(
  email: string,
): Promise<{ message: string }> {
  return apiRequest("/api/v1/auth/resend-confirmation", {
    method: "POST",
    body: { email },
    skipCsrf: true,
  });
}

export async function listPromos(): Promise<
  import("./types.js").PromoRow[]
> {
  const res = await apiRequest<{ promos: import("./types.js").PromoRow[] }>(
    "/api/v1/admin/promos",
  );
  return res.promos ?? [];
}

export async function createPromo(input: {
  code: string;
  type: string;
  value: number;
  maxRedemptions?: number;
  note?: string;
}): Promise<import("./types.js").PromoRow> {
  const res = await apiRequest<{ promo: import("./types.js").PromoRow }>(
    "/api/v1/admin/promos",
    { method: "POST", body: input },
  );
  return res.promo;
}

export async function setPromoActive(
  code: string,
  active: boolean,
): Promise<import("./types.js").PromoRow> {
  const path = active
    ? `/api/v1/admin/promos/${encodeURIComponent(code)}/enable`
    : `/api/v1/admin/promos/${encodeURIComponent(code)}/disable`;
  const res = await apiRequest<{ promo: import("./types.js").PromoRow }>(path, {
    method: "POST",
    body: {},
  });
  return res.promo;
}

// ── PATs ──────────────────────────────────────────────────────────────────

export async function listTokens(): Promise<PatMeta[]> {
  const res = await apiRequest<{ tokens: PatMeta[] }>("/api/v1/tokens");
  return res.tokens ?? [];
}

export async function mintToken(input: {
  name: string;
  expiresInDays?: number;
  /** When set, dual-written if server `pats.scopesWrite` is on (B0+). */
  scopes?: PatScope[];
}): Promise<MintedPat> {
  const body: Record<string, unknown> = {
    name: input.name,
  };
  if (input.expiresInDays !== undefined) {
    body["expiresInDays"] = input.expiresInDays;
  }
  if (input.scopes !== undefined) {
    body["scopes"] = input.scopes;
  }
  return apiRequest<MintedPat>("/api/v1/tokens", {
    method: "POST",
    body,
  });
}

export async function revokeToken(tokenId: string): Promise<void> {
  await apiRequest<null>(
    `/api/v1/tokens/${encodeURIComponent(tokenId)}`,
    { method: "DELETE" },
  );
}

// ── MCP clients (OAuth + PAT connections) ─────────────────────────────────

export async function listMcpClients(): Promise<McpClient[]> {
  const res = await apiRequest<{ clients: McpClient[] }>("/api/v1/mcp-clients");
  return res.clients ?? [];
}

export async function disconnectMcpClient(clientId: string): Promise<void> {
  await apiRequest<{ ok?: boolean }>(
    `/api/v1/mcp-clients/${encodeURIComponent(clientId)}`,
    { method: "DELETE" },
  );
}

// ── Invites (B3 accept SPA) ───────────────────────────────────────────────

export type InvitePreview = {
  email: string;
  status: string;
  role?: string;
  expiresAt?: string;
  inviteId?: string;
};

export type InviteAcceptResult = {
  id?: string;
  email: string;
  status: string;
  userId?: string;
  role?: string;
  inviteId?: string;
};

/**
 * Pre-login safe metadata for accept UI. Does not consume the invite.
 * Token goes in the query string (GET); never log it.
 */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const q = new URLSearchParams({ token });
  return apiRequest<InvitePreview>(
    `/api/v1/invites/preview?${q.toString()}`,
  );
}

/**
 * Accept invite after BFF session login (CSRF required when cookie present).
 * Anonymous POST returns pending_login without consuming.
 */
export async function acceptInvite(
  token: string,
): Promise<InviteAcceptResult> {
  return apiRequest<InviteAcceptResult>("/api/v1/invites/accept", {
    method: "POST",
    body: { token },
  });
}

// ── Attachment upload sessions (SPA compose B8) ───────────────────────────

export async function createUpload(input: {
  filename: string;
  contentType: string;
  size: number;
}): Promise<CreateUploadResponse> {
  return apiRequest<CreateUploadResponse>("/api/v1/attachments/uploads", {
    method: "POST",
    body: input,
  });
}

export async function listUploads(): Promise<UploadSession[]> {
  const res = await apiRequest<{ uploads: UploadSession[] }>(
    "/api/v1/attachments/uploads",
  );
  return res.uploads ?? [];
}

export async function completeUpload(
  uploadId: string,
): Promise<UploadSession> {
  return apiRequest<UploadSession>(
    `/api/v1/attachments/uploads/${encodeURIComponent(uploadId)}/complete`,
    { method: "POST" },
  );
}

export async function abortUpload(uploadId: string): Promise<UploadSession> {
  return apiRequest<UploadSession>(
    `/api/v1/attachments/uploads/${encodeURIComponent(uploadId)}/abort`,
    { method: "POST" },
  );
}

/**
 * Browser PUT bytes to a presigned S3 URL (no cookies / no CSRF).
 * Must send Content-Type and Content-Length exactly as requiredHeaders.
 */
export async function putToPresignedUrl(
  putUrl: string,
  file: Blob,
  requiredHeaders: { "content-type": string; "content-length": string },
): Promise<void> {
  const res = await fetch(putUrl, {
    method: "PUT",
    headers: {
      "content-type": requiredHeaders["content-type"],
      "content-length": requiredHeaders["content-length"],
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(
      res.status,
      "s3_put_failed",
      text
        ? `S3 upload failed (${res.status}): ${text.slice(0, 200)}`
        : `S3 upload failed (${res.status})`,
    );
  }
}

/**
 * Full attach flow: create session → PUT → complete (HeadObject).
 * On failure after create, best-effort abort.
 */
export async function uploadComposeFile(file: File): Promise<UploadSession> {
  const contentType = file.type || "application/octet-stream";
  const session = await createUpload({
    filename: file.name || "attachment",
    contentType,
    size: file.size,
  });
  try {
    await putToPresignedUrl(session.putUrl, file, session.requiredHeaders);
    return await completeUpload(session.uploadId);
  } catch (err) {
    await abortUpload(session.uploadId).catch(() => undefined);
    throw err;
  }
}

// ── Audit (own events) ────────────────────────────────────────────────────

export type AuditPage = {
  events: AuditEvent[];
  nextCursor?: string;
};

/** GET /api/v1/audit — session user's recent audit events (newest first). */
export async function listAudit(opts?: {
  limit?: number;
  cursor?: string;
}): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  const path = qs ? `/api/v1/audit?${qs}` : "/api/v1/audit";
  const res = await apiRequest<{
    events?: AuditEvent[];
    nextCursor?: string;
  }>(path);
  const page: AuditPage = { events: res.events ?? [] };
  if (typeof res.nextCursor === "string" && res.nextCursor) {
    page.nextCursor = res.nextCursor;
  }
  return page;
}

// ── Admin anomaly flags ───────────────────────────────────────────────────

export type AdminFlagsPage = {
  flags: AdminFlag[];
  nextCursor?: string;
};

/** GET /api/v1/admin/flags — owner/admin only. */
export async function listAdminFlags(opts?: {
  limit?: number;
  cursor?: string;
  status?: FlagStatus;
}): Promise<AdminFlagsPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.status) params.set("status", opts.status);
  const qs = params.toString();
  const path = qs
    ? `/api/v1/admin/flags?${qs}`
    : "/api/v1/admin/flags";
  const res = await apiRequest<{
    flags?: AdminFlag[];
    nextCursor?: string;
  }>(path);
  const page: AdminFlagsPage = { flags: res.flags ?? [] };
  if (typeof res.nextCursor === "string" && res.nextCursor) {
    page.nextCursor = res.nextCursor;
  }
  return page;
}

/** POST /api/v1/admin/flags/:flagId/ack — owner/admin only. */
export async function ackAdminFlag(flagId: string): Promise<AdminFlag> {
  return apiRequest<AdminFlag>(
    `/api/v1/admin/flags/${encodeURIComponent(flagId)}/ack`,
    { method: "POST" },
  );
}

// ── Admin invites (productized signup) ────────────────────────────────────

/** GET /api/v1/admin/invites — owner/admin; requires invites.productized. */
export async function listAdminInvites(): Promise<AdminInvite[]> {
  const res = await apiRequest<{ invites?: AdminInvite[] }>(
    "/api/v1/admin/invites",
  );
  return res.invites ?? [];
}

/** POST /api/v1/admin/invites — returns one-time inviteUrl. */
export async function createAdminInvite(input: {
  email: string;
  role?: "user" | "admin";
}): Promise<AdminInviteCreated> {
  return apiRequest<AdminInviteCreated>("/api/v1/admin/invites", {
    method: "POST",
    body: input,
  });
}

/** POST /api/v1/admin/invites/:id/resend — rotates token; new inviteUrl once. */
export async function resendAdminInvite(
  inviteId: string,
): Promise<AdminInviteCreated> {
  return apiRequest<AdminInviteCreated>(
    `/api/v1/admin/invites/${encodeURIComponent(inviteId)}/resend`,
    { method: "POST" },
  );
}

/** DELETE /api/v1/admin/invites/:id — revoke pending invite. */
export async function revokeAdminInvite(
  inviteId: string,
): Promise<AdminInvite> {
  return apiRequest<AdminInvite>(
    `/api/v1/admin/invites/${encodeURIComponent(inviteId)}`,
    { method: "DELETE" },
  );
}

// ── Admin users ───────────────────────────────────────────────────────────

/** GET /api/v1/admin/users — owner/admin. */
export async function listAdminUsers(): Promise<AdminUser[]> {
  const res = await apiRequest<{ users?: AdminUser[] }>(
    "/api/v1/admin/users",
  );
  return res.users ?? [];
}

/** POST /api/v1/admin/users/:id/disable */
export async function disableAdminUser(
  userId: string,
): Promise<AdminUser & { tokenVersion?: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/disable`,
    { method: "POST" },
  );
}

/** POST /api/v1/admin/users/:id/enable */
export async function enableAdminUser(
  userId: string,
): Promise<AdminUser & { tokenVersion?: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/enable`,
    { method: "POST" },
  );
}

/** POST /api/v1/admin/users/:id/revoke-pats */
export async function revokeAdminUserPats(
  userId: string,
): Promise<{ userId: string; tokenVersion: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/revoke-pats`,
    { method: "POST" },
  );
}

/** PATCH /api/v1/admin/users/:id — owner only; body { role }. */
export async function patchAdminUserRole(
  userId: string,
  role: UserRole,
): Promise<AdminUser> {
  return apiRequest<AdminUser>(
    `/api/v1/admin/users/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: { role } },
  );
}

// ── Join the Test (public waitlist) ───────────────────────────────────────

export type JoinRequestResult = {
  ok: boolean;
  status?: string;
  message?: string;
};

export type JoinRequestRow = {
  email: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  name?: string;
  note?: string;
  company?: string;
  wantsGmail?: boolean;
  gmailEmail?: string;
  googleTestUserStatus?: string;
  googleTestUserNote?: string;
};

export type GoogleTestUserActionResult = JoinRequestRow & {
  google?: {
    status: string;
    email?: string;
    consoleUrl?: string;
    note?: string;
  };
};

/** POST /api/v1/join-requests — public (no session). */
export async function submitJoinRequest(input: {
  email: string;
  wantsGmail: boolean;
  gmailEmail?: string;
  name?: string;
  company?: string;
  note?: string;
  /** Honeypot — leave empty for real users. */
  website?: string;
}): Promise<JoinRequestResult> {
  return apiRequest<JoinRequestResult>("/api/v1/join-requests", {
    method: "POST",
    body: input,
  });
}

/** GET /api/v1/admin/join-requests — owner/admin. */
export async function listJoinRequests(opts?: {
  status?: string;
}): Promise<JoinRequestRow[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set("status", opts.status);
  const qs = params.toString();
  const path = qs
    ? `/api/v1/admin/join-requests?${qs}`
    : "/api/v1/admin/join-requests";
  const res = await apiRequest<{ requests?: JoinRequestRow[] }>(path);
  return res.requests ?? [];
}

/** PATCH /api/v1/admin/join-requests/:email */
export async function patchJoinRequest(
  email: string,
  body: {
    status?: "pending" | "invited" | "dismissed";
    googleTestUserStatus?: string;
    googleTestUserNote?: string;
  },
): Promise<JoinRequestRow> {
  return apiRequest<JoinRequestRow>(
    `/api/v1/admin/join-requests/${encodeURIComponent(email)}`,
    { method: "PATCH", body },
  );
}

/**
 * POST …/google-test-user — best-effort add to Google OAuth test users.
 * Always returns consoleUrl when manual step is still required.
 */
export async function addJoinRequestGoogleTestUser(
  email: string,
): Promise<GoogleTestUserActionResult> {
  return apiRequest<GoogleTestUserActionResult>(
    `/api/v1/admin/join-requests/${encodeURIComponent(email)}/google-test-user`,
    { method: "POST" },
  );
}
